import React, { lazy, useEffect, Suspense, useState, useRef } from "react"
import PropTypes from "prop-types"
import styled, { keyframes } from "styled-components"
import PrimaryLayout from "@src/layouts/primary"
import { ViewportProvider } from "@src/layouts/viewport"
import { useTourBreadcrumbs } from "@src/hooks/useTourBreadcrumbs"
import PageHelmet, { PageSeoPropTypes } from "@src/components/page-helmet"
import { paginateTitle, generateMeta } from "@src/utils/page-data"
import theme from "@src/styles/theme"

const ResponsiveSection = lazy(
  () => import("@src/components/container-responsive-section"),
)
import { ImagePropTypes } from "@src/components/core-image"
const TourListWithFilters = lazy(
  () => import("@src/components/tour-page/content-tour-list-with-filters-v2"),
)
import { TourDetailTilePropTypes } from "@src/components/content-tour-detail-tile"
import { PostTileQueryPropTypes } from "@src/components/content-post-tile"
import { SITE_DOMAIN } from "@src/utils/constants"
import { TOUR_SORT_BY_OPTION_DEFAULT } from "@src/utils/constants/tour-list-options"
import BreadcrumbsV2 from "@src/components/nav-content-breadcrumbs-v2"
import generateBreadcrumbList from "@src/utils/structured-data/generateBreadcrumbList"
import { window } from "browser-monads"
import HeroTour from "@src/components/tour-page/hero"
import Media from "@src/components/tour-page/media"
const Information = lazy(() => import("@src/components/tour-page/information"))
const HotjarScript = lazy(() => import("@src/scripts/hotjar"))
import SocialProofReasons from "@src/components/content-social-proof-reasons-v2"
import { difficulties } from "@src/utils/constants/activity-list-options"

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const LoadingSpinner = styled.div`
  border: 8px solid rgba(0, 0, 0, 0.1);
  border-top: 8px solid ${props => props.theme.greenShade};
  border-radius: 50%;
  width: 80px;
  height: 80px;
  animation: ${spin} 1s linear infinite;
  display: inline-block;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30vh;
`

const PageColor = styled.div`
  background: #f9fafb;
`

const TitleRegion = styled.h1`
  color: ${theme.blackShade};
  font-size: 46px;
  font-style: normal;
  font-weight: 900;
  line-height: 64px;
  letter-spacing: 0.5px;
`

const ToursLayoutLazy = ({
  regionName,
  pageName,
  pageSeo,
  headerText,
  toursParentUri,
  tourData,
  toursPageNumber,
  toursPageCount,
  toursPerPage,
  totalTours,
  tourListPromos,
  regionId,
  categoryId,
  faqs,
  topSeoRegions,
  seoTextBottomNew,
  regionSlug,
  bottomSeoData: seoData,
  skipForBottomSeo: skipForSeo,
  subtitle,
}) => {
  const { headerBreadcrumbs } = useTourBreadcrumbs(regionId, categoryId)

  const rawBreadcrumbStructuredData = headerBreadcrumbs.map(
    (breadcrumb, index) => {
      return {
        name: breadcrumb.text,
        url: `${SITE_DOMAIN}${breadcrumb.link}`,
        position: index + 1,
      }
    },
  )

  const [lazyLoad, setLazyLoad] = useState(false)

  useEffect(() => {
    if (window.location.href.includes("page")) {
      const timer = setTimeout(() => {
        setLazyLoad(true)
      }, 3000)

      return () => clearTimeout(timer)
    } else {
      setLazyLoad(true)
    }
  }, [])

  const [filters, setFilters] = useState(null)

  useEffect(() => {
    const difficulty = decodeURI(
      new URLSearchParams(window.location.search).get("difficulty"),
    )
    setFilters({
      sorting: TOUR_SORT_BY_OPTION_DEFAULT,
      regions: regionId && [regionId],
      categories: categoryId && [categoryId],
      difficulty:
        difficulty && difficulty !== "null"
          ? [difficulties[difficulty]]
          : undefined,
    })
  }, [lazyLoad, regionId, categoryId])

  const tourListRef = useRef(null)

  // Hiking & Biking Links
  const categories = { hiking: null, biking: null }
  if (seoData?.seoTourCategories && seoData.seoTourCategories.length) {
    const { seoTourCategories } = seoData
    seoTourCategories.forEach(category => {
      if (
        category.id !== categoryId &&
        !skipForSeo.includes(`/tours/${regionSlug}-${category.slug}/`)
      ) {
        categories[category.name.toLowerCase()] = {
          url: `/tours/${regionSlug}-${category.slug}/`,
          name: regionName,
        }
      }
    })
  }

  return (
    <PageColor>
      <PrimaryLayout pagePath={toursParentUri}>
        <PageHelmet
          title={paginateTitle(pageSeo.title, toursPageNumber, toursPageCount)}
          meta={generateMeta(toursParentUri, pageSeo, {
            pageNumber: toursPageNumber,
            pageCount: toursPageCount,
          })}
          structuredData={generateBreadcrumbList(rawBreadcrumbStructuredData)}
        />
        {lazyLoad && (
          <Suspense fallback={null}>
            <HotjarScript />
          </Suspense>
        )}
        <br />
        <ResponsiveSection>
          <BreadcrumbsV2 breadcrumbItems={headerBreadcrumbs} />
        </ResponsiveSection>

        <ResponsiveSection>
          <TitleRegion>{pageName}</TitleRegion>

          <HeroTour
            pageName={pageName}
            headerText={headerText}
            subtitle={subtitle}
            {...categories}
            topSeoRegions={topSeoRegions}
          />
        </ResponsiveSection>

        <Media />

        {/* {lazyLoad && (
          <Suspense fallback={null}>
            <ExitPopup region={pageName} />
          </Suspense>
        )} */}

        {!lazyLoad && (
          <LoadingWrapper>
            <LoadingSpinner />
          </LoadingWrapper>
        )}

        <Suspense
          fallback={
            null
            // <LoadingWrapper>
            //   <LoadingSpinner />
            // </LoadingWrapper>
          }
        >
          {/* {(lazyLoad || !isMobile) && ( */}
          {/* This component hurts desktop SEO badly */}
          {lazyLoad && (
            <ViewportProvider>
              <div ref={tourListRef}>
                <TourListWithFilters
                  data={{
                    filters: {
                      ...filters,
                    },
                    tours: tourData,
                    pageBaseUri: toursParentUri,
                    pageNumber: toursPageNumber,
                    pageCount: toursPageCount,
                    perPage: toursPerPage,
                    totalCount: totalTours,
                  }}
                  tourListPromos={tourListPromos}
                  variant="vertical"
                />
              </div>
            </ViewportProvider>
          )}
        </Suspense>
        {/* {lazyLoad && (
          <Suspense fallback={<div>Loading...</div>}>
            <ResponsiveSection>
              <Specialist />
            </ResponsiveSection>
          </Suspense>
        )}
        {lazyLoad && (
          <Suspense fallback={<div>Loading...</div>}>
            <ResponsiveSection>
              <BestTrips />
            </ResponsiveSection>
          </Suspense>
        )} */}
        <SocialProofReasons />
        {lazyLoad && (regionName || seoTextBottomNew || faqs) && (
          <Suspense fallback={<div>Loading...</div>}>
            <ResponsiveSection>
              <Information
                regionName={regionName}
                seoTextBottomNew={seoTextBottomNew}
                faqs={faqs}
              />
            </ResponsiveSection>
          </Suspense>
        )}

        {/* {lazyLoad && (
          <Suspense fallback={<div>Loading...</div>}>
            <ResponsiveSection>
              <RecentStories />
            </ResponsiveSection>
          </Suspense>
        )} */}
      </PrimaryLayout>
    </PageColor>
  )
}
ToursLayoutLazy.propTypes = {
  regionName: PropTypes.string,
  regionSlug: PropTypes.string,
  regionUri: PropTypes.string,
  categoryName: PropTypes.string,
  categorySlug: PropTypes.string,
  categoryUri: PropTypes.string,
  pageName: PropTypes.string,
  pageSeo: PropTypes.shape({ title: PropTypes.string, ...PageSeoPropTypes }),
  headerText: PropTypes.string,
  toursParentUri: PropTypes.string,
  tourData: PropTypes.arrayOf(PropTypes.shape({ ...TourDetailTilePropTypes })),
  toursPageNumber: PropTypes.number,
  toursPageCount: PropTypes.number,
  toursPerPage: PropTypes.number,
  totalTours: PropTypes.number,
  tourListPromos: PropTypes.objectOf(PropTypes.func),
  regionId: PropTypes.number,
  categoryId: PropTypes.number,
  featuredTourRegions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      uri: PropTypes.string,
      images: PropTypes.shape({
        featureTile: PropTypes.shape({ ...ImagePropTypes }),
      }),
    }),
  ),
  posts: PropTypes.arrayOf(PropTypes.shape({ ...PostTileQueryPropTypes })),
  faqs: PropTypes.shape({
    title: PropTypes.string,
    faqs: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string,
        answer: PropTypes.string,
      }),
    ),
  }),
  seoTextBottomNew: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  bottomSeoData: PropTypes.shape({
    faq: PropTypes.shape({
      title: PropTypes.string,
      faqs: PropTypes.arrayOf(
        PropTypes.shape({
          question: PropTypes.string,
          answer: PropTypes.string,
        }),
      ),
    }),
    seoTextBottomNew: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string,
        title: PropTypes.string,
      }),
    ),
    seoTourCategories: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
        uri: PropTypes.string,
      }),
    ),
  }),
  skipForBottomSeo: PropTypes.arrayOf(PropTypes.string),
  replaceURLs: PropTypes.object,
  tourRegionCategoriesList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      parentId: PropTypes.number,
      name: PropTypes.string,
      slug: PropTypes.string,
      uri: PropTypes.string,
      tourCategories: PropTypes.arrayOf(PropTypes.number),
    }),
  ),
}
export default ToursLayoutLazy
